import { createContext, useContext } from "react";
import { AppealThread } from "../dtos";
import { AppealAnchor } from "../utils/appeals";

/**
 * §Review-appeal: the appeal surface of the step being rendered, reachable from any depth.
 *
 * A context rather than props on purpose. An appeal renders beside the item it challenges, and those items
 * sit three and four levels down — a file row inside DocumentField, an owner card inside TwoSidedDocument,
 * a field inside FormFieldInput's nine FieldShell variants. Threading four props to each would mean matching
 * a call-site count by hand, which is exactly how the workflow-manager half of this feature shipped a
 * component that was correct in isolation and never received data (fixed in 52fa62e). Consumers ask for what
 * they need instead, so a new anchor can never be half-wired.
 *
 * The default is INERT, not a throw: DocumentField also renders inside the KYC modal, outside any step form,
 * and there the right answer is simply no appeal surface.
 */
export interface AppealsContextValue {
  caseUid: string;
  stepUid: string;
  /** Threads challenging this exact item, oldest first. */
  threadsFor: (anchor: AppealAnchor) => AppealThread[];
  /** May the caller raise a NEW appeal on this item right now? Server-gated, then window-checked. */
  canOpen: (anchor: AppealAnchor) => boolean;
  /** Both writes RESOLVE only on success and reject on failure, so a composer can keep the typed text
   * when the server refuses (a 409 duplicate, a closed window) instead of losing the paragraph. */
  open: (anchor: AppealAnchor, content: string, attachmentUids?: string[]) => Promise<unknown>;
  reply: (appealUid: string, content: string, attachmentUids?: string[]) => Promise<unknown>;
  busy: boolean;
}

const INERT_APPEALS: AppealsContextValue = {
  caseUid: "",
  stepUid: "",
  threadsFor: () => [],
  canOpen: () => false,
  open: () => Promise.resolve(),
  reply: () => Promise.resolve(),
  busy: false,
};

export const AppealsContext = createContext<AppealsContextValue>(INERT_APPEALS);

export const useAppeals = (): AppealsContextValue => useContext(AppealsContext);
